import { authHeader, deviceFingerprint, trustedDevice } from '@/_helpers';
import { API_URL } from "../config/app";
import { userService } from './user.service';

const URL = API_URL

// Doble factor. Las funciones de login son el segundo paso después de la contraseña:
// al usuario lo identifica el two_factor_token que devolvió /users/login. Las demás
// son de la página de Seguridad, con la sesión ya abierta.
// Configurar o cambiar el método siempre empieza confirmando el correo (send*EmailCode y
// verify*Email); después se elige el método y se confirma (con correo, sin código).
export const twoFactorService = {
  verifyLogin,
  sendLoginEmailCode,
  sendLoginSetupEmailCode,
  verifyLoginSetupEmail,
  startLoginSetup,
  confirmLoginSetup,
  getStatus,
  sendChangeEmailCode,
  verifyChangeEmail,
  startChange,
  confirmChange,
  listTrustedDevices,
  forgetTrustedDevice,
  forgetTrustedDevices
};

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

// verifyLogin y confirmLoginSetup son las que abren la sesión: llevan la identificación
// del navegador (X-Device-Id y X-Client-Ref) para el historial de dispositivos, y van con
// credentials para que el navegador mande y guarde la cookie que pone la API. Con
// remember ("Recordar por 30 días") la respuesta trae trusted_device.
function verifyLogin(token, code, remember) {
  return deviceFingerprint.withDeviceHeader(JSON_HEADERS)
    .then(headers => post('/users/login/two_factor', { two_factor_token: token, code, remember_device: !!remember }, headers, handleLoginStepResponse, { credentials: 'include' }));
}

function sendLoginEmailCode(token) {
  return post('/users/login/two_factor/email', { two_factor_token: token }, JSON_HEADERS, handleLoginStepResponse);
}

// Responde {email_hint}.
function sendLoginSetupEmailCode(token) {
  return post('/users/login/two_factor/setup/email', { two_factor_token: token }, JSON_HEADERS, handleLoginStepResponse);
}

function verifyLoginSetupEmail(token, code) {
  return post('/users/login/two_factor/setup/email/verify', { two_factor_token: token, code }, JSON_HEADERS, handleLoginStepResponse);
}

function startLoginSetup(token, method) {
  return post('/users/login/two_factor/setup', { two_factor_token: token, method }, JSON_HEADERS, handleLoginStepResponse);
}

function confirmLoginSetup(token, code, remember) {
  return deviceFingerprint.withDeviceHeader(JSON_HEADERS)
    .then(headers => post('/users/login/two_factor/setup/confirm', { two_factor_token: token, code, remember_device: !!remember }, headers, handleLoginStepResponse, { credentials: 'include' }));
}

function getStatus() {
  const requestOptions = {
    method: 'GET',
    headers: authHeader()
  };

  return fetch(URL + "/two_factor", requestOptions)
    .then(handleSessionResponse)
    .then(response => response.data[0].attributes);
}

// Responde {email_hint}.
function sendChangeEmailCode() {
  return post('/two_factor/setup/email', {}, authHeader(), handleSessionResponse);
}

function verifyChangeEmail(code) {
  return post('/two_factor/setup/email/verify', { code }, authHeader(), handleSessionResponse);
}

function startChange(method) {
  return post('/two_factor/setup', { method }, authHeader(), handleSessionResponse);
}

function confirmChange(code) {
  return post('/two_factor/setup/confirm', { code }, authHeader(), handleSessionResponse);
}

// Navegadores con "Recordar por 30 días" vigente. Se manda el token de este navegador
// para que la API marque cuál es (current). Cada uno: {id, attributes}.
function listTrustedDevices() {
  const headers = authHeader();
  const token = trustedDevice.get();
  if (token) {
    headers['X-Trusted-Device'] = token;
  }
  return fetch(URL + "/two_factor/trusted_devices", { method: 'GET', headers })
    .then(handleSessionResponse)
    .then(response => response.data || []);
}

// Olvida un navegador: vuelve a pedir el código.
function forgetTrustedDevice(id) {
  return fetch(URL + "/two_factor/trusted_devices/" + encodeURIComponent(id), { method: 'DELETE', headers: authHeader() })
    .then(handleSessionResponse);
}

// Olvida todos los navegadores con "Recordar por 30 días": vuelven a pedir el código.
function forgetTrustedDevices() {
  return fetch(URL + "/two_factor/trusted_devices", { method: 'DELETE', headers: authHeader() })
    .then(handleSessionResponse);
}

function post(path, body, headers, handler, options = {}) {
  const requestOptions = Object.assign({
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  }, options);

  return fetch(URL + path, requestOptions).then(handler);
}

function errorDetail(data) {
  return (data && data.errors && data.errors.length && data.errors[0].detail) || "No pudimos completar la verificación, intenta de nuevo";
}

// En el login un 401 no es sesión vencida sino el two_factor_token: hay que volver a
// poner la contraseña. Se rechaza con {status, message} para que la pantalla lo distinga.
function handleLoginStepResponse(response) {
  return response.text().then(text => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      return Promise.reject({ status: response.status, message: errorDetail(data) });
    }
    return data;
  });
}

function handleSessionResponse(response) {
  return response.text().then(text => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      if (response.status === 401) {
        userService.refreshToken();
        return Promise.reject({ status: 401, message: "Intentando recuperar la sesión" });
      }
      return Promise.reject({ status: response.status, message: errorDetail(data) });
    }
    return data;
  });
}
